import { FiLanguage } from '../../../../contexts/LocaleContext';
import { type Breed, type BreedWeight } from './BreedDataset';

export type SizingDataset = Pick<
  typeof import('./BreedDataset'),
  'Breeds' | 'GenericAdultsSizingGuide' | 'GenericPuppySizingGuide'
>;

/** The breed tables are large, so they load when a guide opens rather than with the page. */
export async function loadSizingDataset(): Promise<SizingDataset> {
  const { Breeds, GenericAdultsSizingGuide, GenericPuppySizingGuide } = await import('./BreedDataset');
  return { Breeds, GenericAdultsSizingGuide, GenericPuppySizingGuide };
}

export function getBreedName(breed: Breed, language: FiLanguage): string {
  return breed.locale[language] ?? breed.locale[FiLanguage.EN_US] ?? '';
}

/**
 * What owners of this breed at this weight bought. A dog more than a deviation under the breed's
 * average reads as a puppy and uses the puppy table; a weight the breed table lacks falls back to
 * the generic one.
 */
export function sizingDistributionFor(
  dataset: SizingDataset,
  breed: Breed,
  weightLbs: number,
): { distribution?: BreedWeight; isPuppy: boolean } {
  if (!breed.sizing) return { isPuppy: false };
  const { averageWeight, weightStdDev, adultWeights, puppyWeights } = breed.sizing;
  const isPuppy = weightLbs <= averageWeight - weightStdDev;
  const ceiledWeight = Math.ceil(weightLbs / 10) * 10;
  const fromBreed = (isPuppy ? puppyWeights : adultWeights).find((row) => row.weight === ceiledWeight);
  if (fromBreed) return { distribution: fromBreed, isPuppy };
  const fallback = (isPuppy ? dataset.GenericPuppySizingGuide : dataset.GenericAdultsSizingGuide)[String(ceiledWeight)];
  return { distribution: fallback ? { weight: ceiledWeight, ...fallback } : undefined, isPuppy };
}
